import { makeStyles } from "@material-ui/core/styles";
import CardContent from "@material-ui/core/CardContent";
import { Typography } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import { useEffect, useState } from "react";
import GenericError from "../error/genericError";
import CircularLoader from "../loading/circularLoader";
import { useDataProvider } from "react-admin";

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        backgroundColor: theme.surface.secondary,
        marginBottom: '24px',
    },
    content: {
        width: '100%',
        padding: '24px'
    },
    header: {
        fontSize: '20px',
        lineHeight: '26px',
        fontWeight: 700,
        color: '#FFFFFF',
        paddingBottom: '24px',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    rowContainer: {
        '& > h6': {
            fontWeight: 400,
            color: '#FFFFFF',
            opacity: 0.5,
            marginBottom: '8px'
        },
        '& > p': {
            fontWeight: 400,
            color: '#FFFFFF',
            fontSize: '16px',
            lineHeight: '21px',
            marginBottom: '32px'
        },
        '&:last-child': {
            '& > p': {
                marginBottom: '12px'
            }
        }
    },
}));

const formatEmploymentStatus = (status) => {
    if (!status) return '-';
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const formatAccountPurpose = (purpose) => {
    if (!purpose) return '-';
    return purpose.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const formatSourceOfFunds = (source) => {
    if (!source) return '-';
    return source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const formatExpectedMonthlyPayments = (range) => {
    if (!range) return '-';
    
    const rangeMap = {
        'RANGE_0_4999': 'Under $4,999',
        'RANGE_5000_9999': '$5,000 - $9,999',
        'RANGE_10000_49999': '$10,000 - $49,999',
        'RANGE_50000_PLUS': 'Above $50,000'
    };
    
    return rangeMap[range] || range;
};

const ComplianceDetailsCard = ({ customerId }) => {
    const classes = useStyles();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const dataProvider = useDataProvider();

    useEffect(() => {
        if (data == null) {
            setLoading(true);
            dataProvider.getOne('kycVerification', { customerId })
                .then(({ data }) => {
                    if (data.error) throw data.error;
                    else {
                        setData(data?.result?.getKycVerificationDetails);
                        setError(null);
                    }
                }).catch((error) => {
                    setError(error);
                    console.error('Error fetching compliance details:', error);
                }).finally(() => {
                    setLoading(false);
                });
        }
    }, [data, customerId, dataProvider]);

    const kycDetails = data?.kyc;
    const additionalDetails = kycDetails?.additionalDetails;

    const rowData = [
        {
            label: 'Tax ID Type',
            value: kycDetails?.taxIdType || '-'
        },
        {
            label: 'Tax ID Country',
            value: kycDetails?.taxIdCountryCode || '-'
        },
        {
            label: 'Expected Monthly Payments',
            value: formatExpectedMonthlyPayments(kycDetails?.expectedMonthlyPayments)
        },
        {
            label: 'Employment Status',
            value: formatEmploymentStatus(additionalDetails?.employmentStatus)
        },
        {
            label: 'Occupation Code',
            value: additionalDetails?.mostRecentOccupationCode || '-'
        },
        {
            label: 'Account Purpose',
            value: formatAccountPurpose(additionalDetails?.accountPurpose)
        },
        {
            label: 'Is Intermediary',
            value: additionalDetails?.isIntermediary != null 
                ? (additionalDetails.isIntermediary ? 'Yes' : 'No') 
                : '-'
        },
        {
            label: 'SEPA Enabled',
            value: kycDetails?.sepaEnabled != null 
                ? (kycDetails.sepaEnabled ? 'Yes' : 'No') 
                : '-'
        },
        {
            label: 'Country of Residence',
            value: kycDetails?.countryOfResidence || '-'
        },
        {
            label: 'Document Type',
            value: kycDetails?.documentType || '-'
        },
        {
            label: 'Document Country',
            value: kycDetails?.documentCountryCode || '-'
        },
    ];

    return (
        <Card elevation={0} className={classes.root}>
            <CardContent className={classes.content}>
                <Typography className={classes.header} variant="h3">Compliance Details</Typography>
                {
                    error ? 
                        <div className={classes.container}>
                            <GenericError onClick={() => setData(null)} />
                        </div> :
                    loading ? 
                        <div className={classes.container}>
                            <CircularLoader trigger={loading} loaderSize='20px' />
                        </div> :
                    !data || !kycDetails ?
                        <div className={classes.container}>
                            <Typography variant="body1" style={{ color: '#FFFFFF', opacity: 0.5 }}>
                                No compliance data available for this customer
                            </Typography>
                        </div> :
                        <div className={classes.container}>
                            {rowData.map(({ value, label }, index) => (
                                <div className={classes.rowContainer} key={index}>
                                    <Typography variant="h6">{label}</Typography>
                                    <Typography variant="body1">{value}</Typography>
                                </div>
                            ))}
                        </div>
                }
            </CardContent>
        </Card>
    );
};

export default ComplianceDetailsCard;

